import React, { useState, useEffect, useRef } from "react";
import { Player, ControlBar, BigPlayButton, PlayToggle, VolumeMenuButton, ProgressControl, FullscreenToggle, CurrentTimeDisplay, TimeDivider, DurationDisplay } from "video-react";
import "styles/video-react-local.scss";
import { Task } from "@induz/sdk";
import "./videoPlayer.scss";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";

// Assets
import logo from "assets/images/homepage/main/logo.svg";
import logoDark from "assets/images/homepage/main/logoDark.svg";

// Icon Components
const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const getVideoMimeType = (extension) => {
    const map = {
        mp4: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
        webm: 'video/webm', // Generic webm often works better for MSE unless specific codecs are known
        ogg: 'video/ogg',
        ogv: 'video/ogg',
        mov: 'video/mp4', // MOV can often be played as MP4 if h264
        mkv: 'video/x-matroska'
    };
    return map[extension] || `video/${extension}`;
};

const VideoViewer = ({ isVisible, handleCancel, fo, fileName, dupCurrentPath }) => {
    const [loading, setLoading] = useState(false);
    const [videoUrl, setVideoUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [retryTrigger, setRetryTrigger] = useState(0);
    const theme = useTheme();
    const isMobileView = useScreenMobile({ size: 768 });

    const abortControllerRef = useRef(null);
    const mediaSourceRef = useRef(null);
    const sourceBufferRef = useRef(null);

    // Ordered chunks management
    const fetchedChunksRef = useRef({}); // offset -> ArrayBuffer
    const nextAppendOffsetRef = useRef(0);
    const nextFetchOffsetRef = useRef(0);

    const playerRef = useRef(null);
    const fetchIdRef = useRef(0);
    const totalSizeRef = useRef(0);

    const isEvictingRef = useRef(false);
    const isMseModeRef = useRef(true);
    const [duration, setDuration] = useState(0);

    const determinePath = (fo, fileName, dupCurrentPath) => {
        const currentDupPath = dupCurrentPath || [];
        let path = fo.getParentPath() + "/" + fo.getName();
        if (currentDupPath.length === 1 && (currentDupPath[0] === "shared" || currentDupPath[0] === "external_shared")) {
            const uuid = typeof fo.getFileUuId === "function" ? fo.getFileUuId() : null;
            path = fo.getParentPath() + "/" + (uuid || fileName);
        }
        return path;
    };

    const extractDuration = async (fo, totSize, filePath) => {
        try {
            const chunkSize = 4 * 1024 * 1024;
            const metaTask = new Task(totSize, fileName, "Checking Duration");
            const lastChunkSize = totSize % chunkSize === 0 ? chunkSize : totSize % chunkSize;

            // Fetch first and last 4MB to estimate duration accurately
            const firstChunk = await fo.getChunk(0, filePath, metaTask, chunkSize);
            const lastChunk = await fo.getChunk(totSize - lastChunkSize, filePath, metaTask, lastChunkSize);

            const blob = new Blob([firstChunk, lastChunk], { type: fo.type || "video/mp4" });
            const tempUrl = URL.createObjectURL(blob);
            const tempVideo = document.createElement("video");
            tempVideo.src = tempUrl;

            return new Promise((resolve) => {
                tempVideo.onloadedmetadata = () => {
                    const dur = tempVideo.duration;
                    URL.revokeObjectURL(tempUrl);
                    resolve(dur && dur !== Infinity ? dur : null);
                };
                tempVideo.onerror = () => {
                    URL.revokeObjectURL(tempUrl);
                    resolve(null);
                };
                setTimeout(() => {
                    URL.revokeObjectURL(tempUrl);
                    resolve(null);
                }, 5000); // Video metadata can take longer than audio
            });
        } catch (e) {
            console.error("[DurationExt] Extraction failed:", e);
            setError("[DurationExt] Extraction failed:", e);
            return null;
        }
    };

    const cleanupBuffer = () => {
        const sb = sourceBufferRef.current;
        const videoElement = playerRef.current?.video?.video;
        if (!sb || !videoElement || sb.updating || isEvictingRef.current) return;

        const curTime = videoElement.currentTime;
        if (curTime > 120) { // Keep 2 minutes of history
            try {
                const removeEnd = curTime - 60;
                let hasBufferBefore = false;
                for (let i = 0; i < sb.buffered.length; i++) {
                    if (sb.buffered.start(i) < removeEnd) {
                        hasBufferBefore = true;
                        break;
                    }
                }
                if (hasBufferBefore) {
                    isEvictingRef.current = true;
                    console.log(`[MediaSource] Cleaning up old buffer data: 0 to ${removeEnd.toFixed(1)}s`);
                    sb.remove(0, removeEnd);
                }
            } catch (e) {
                console.error("[MediaSource] Eviction error:", e);
                setError("[MediaSource] Eviction error:", e);
            }
        }
    };

    const triggerFallbackDownload = async () => {
        if (!isMseModeRef.current) return; // Already in fallback

        console.log("[VideoStream] Triggering fallback to full download...");
        isMseModeRef.current = false;

        // Cleanup existing MSE
        fetchIdRef.current++;
        if (abortControllerRef.current) abortControllerRef.current.abort();

        if (mediaSourceRef.current && mediaSourceRef.current.readyState === "open") {
            try { mediaSourceRef.current.endOfStream(); } catch (e) { console.error("Ignored EndOfStream error during fallback:", e); }
        }

        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
        }

        setLoading(true);
        setDownloadProgress(0);
        setError(null);

        // Restart fresh
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        const fullStreamPath = determinePath(fo, fileName, dupCurrentPath);

        fetchIdRef.current++;
        fetchIdRef.current++; // ensure unique ID

        try {
            const fullBlob = await startConcurrentFetch(totalSizeRef.current, signal, 0, fetchIdRef.current, fullStreamPath, true);
            if (fullBlob && fetchIdRef.current === fetchIdRef.current) {
                const url = URL.createObjectURL(fullBlob);
                setVideoUrl(url);
                setIsInitialized(true);
                setLoading(false);
            }
        } catch (err) {
            if (!signal.aborted) {
                console.error("[VideoStream] Fallback download failed:", err);
                setError("Failed to download video for native playback. " + (err.message || ''));
            }
        }
    };

    const appendNextAvailable = () => {
        if (!isMseModeRef.current) return;
        const sb = sourceBufferRef.current;
        const ms = mediaSourceRef.current;
        const videoElement = playerRef.current?.video?.video;
        if (!sb || !videoElement || sb.updating || isEvictingRef.current) return;

        const nextOffset = nextAppendOffsetRef.current;
        const chunk = fetchedChunksRef.current[nextOffset];

        if (chunk) {
            try {
                sb.appendBuffer(chunk);
                delete fetchedChunksRef.current[nextOffset];
                nextAppendOffsetRef.current += chunk.byteLength;

                if (nextAppendOffsetRef.current % (16 * 1024 * 1024) === 0) {
                    const buffered = [];
                    for (let i = 0; i < sb.buffered.length; i++) {
                        buffered.push(`[${sb.buffered.start(i).toFixed(1)}-${sb.buffered.end(i).toFixed(1)}]`);
                    }
                    console.log(`[MediaSource] Appended: ${nextAppendOffsetRef.current} bytes. Ranges: ${buffered.join(", ")}`);
                    cleanupBuffer();
                }
            } catch (e) {
                if (e.name === "QuotaExceededError") {
                    console.warn("[MediaSource] Buffer Quota hit. Forcing eviction...");
                    cleanupBuffer();
                } else if (e.name === "NotSupportedError" || (e.message && e.message.includes("appendBuffer"))) {
                    console.warn("[MediaSource] Unfragmented or unsupported MP4 detected. Falling back to full download...", e);
                    triggerFallbackDownload();
                } else {
                    console.error("[MediaSource] Append error:", e);
                    setError("[MediaSource] Append error:", e);
                }
            }
        } else if (nextAppendOffsetRef.current >= totalSizeRef.current && ms?.readyState === "open" && !sb.updating) {
            try {
                ms.endOfStream();
                console.log("[MediaSource] Playback stream finalized.");
            } catch (e) {
                console.warn("[MediaSource] EndOfStream fail (minor):", e);
            }
        }
    };

    useEffect(() => {
        if (!isVisible || !fo) {
            cleanup();
            return;
        }

        const startStreaming = async () => {
            setLoading(true);
            setError(null);
            setIsInitialized(false);
            cleanup();

            const totSize = await fo.getSize();
            totalSizeRef.current = totSize;

            const fullStreamPath = determinePath(fo, fileName, dupCurrentPath);
            console.log("[VideoStream] Launching:", { fileName, fullStreamPath, totSize });

            const extractedDur = await extractDuration(fo, totSize, fullStreamPath);
            if (extractedDur) {
                setDuration(extractedDur);
            }

            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            nextFetchOffsetRef.current = 0;
            nextAppendOffsetRef.current = 0;
            fetchedChunksRef.current = {};

            const fileExtension = fileName?.split(".").pop()?.toLowerCase();
            const mimeType = getVideoMimeType(fileExtension) || fo.type || "video/mp4";

            // For video, we need to be more careful with codecs in MediaSource.isTypeSupported
            // Using a generic check first, but some containers might need specific codecs strings.
            const canPlayMSE = window.MediaSource && MediaSource.isTypeSupported(mimeType);
            const tempVideo = document.createElement("video");
            const canPlayNative = tempVideo.canPlayType(mimeType) !== "";

            if (!canPlayMSE && !canPlayNative) {
                setError(`Format .${fileExtension} is not supported by your browser.`);
                setLoading(false);
                return;
            }

            isMseModeRef.current = canPlayMSE;

            if (canPlayMSE) {
                const ms = new MediaSource();
                mediaSourceRef.current = ms;
                const url = URL.createObjectURL(ms);
                setVideoUrl(url);

                ms.addEventListener("sourceopen", async () => {
                    if (ms.sourceBuffers.length > 0) return;
                    if (extractedDur && ms.readyState === "open") {
                        ms.duration = extractedDur;
                    }

                    try {
                        const sb = ms.addSourceBuffer(mimeType);
                        sourceBufferRef.current = sb;

                        sb.addEventListener("updateend", () => {
                            isEvictingRef.current = false;
                            appendNextAvailable();
                        });

                        sb.addEventListener("error", (e) => {
                            console.error("[SourceBuffer] Pipe error:", e);
                            // SourceBuffer async errors usually mean bad format/unfragmented
                            console.warn("[MediaSource] Async pipe error. Triggering fallback...");
                            triggerFallbackDownload();
                        });

                        fetchIdRef.current++;
                        startConcurrentFetch(totSize, signal, 0, fetchIdRef.current, fullStreamPath);
                    } catch (e) {
                        console.log("[SourceBuffer] Creation error:", e);
                        setError("Codec or format not supported by MediaSource. Try another browser or file.");
                        setLoading(false);
                    }
                });
            } else {
                console.log(`[VideoStream] MSE not supported for ${mimeType}. Falling back to full download.`);
                setLoading(true);
                setDownloadProgress(0);
                fetchIdRef.current++;
                const fullBlob = await startConcurrentFetch(totSize, signal, 0, fetchIdRef.current, fullStreamPath, true);
                if (fullBlob && fetchIdRef.current === fetchIdRef.current) {
                    const url = URL.createObjectURL(fullBlob);
                    setVideoUrl(url);
                    setIsInitialized(true);
                    setLoading(false);
                }
            }
        };

        startStreaming();
        return () => cleanup();
    }, [isVisible, fo, fileName, retryTrigger]);

    const handleRetry = () => {
        setRetryTrigger(prev => prev + 1);
    };

    const startConcurrentFetch = async (totSize, signal, startOffset, currentFetchId, path, isFallbackMode = false) => {
        const dataTask = new Task(totSize, fileName, isFallbackMode ? "Downloading Full Video" : "Streaming Video");
        const chunkSize = 4 * 1024 * 1024; // 4MB
        const maxConcurrency = 12;

        const accumulatedChunks = isFallbackMode ? {} : null;

        nextFetchOffsetRef.current = startOffset;
        let activeDownloads = 0;
        let errorOccurred = null;
        const fetchPool = [];

        console.log(`[FetchPool] Active [${currentFetchId}] at offset ${startOffset} with ${maxConcurrency} workers`);

        const downloadChunkWithRetry = async (offset, size, retries = 1) => {
            if (errorOccurred || signal.aborted || currentFetchId !== fetchIdRef.current) return;
            for (let i = 0; i < retries; i++) {
                try {
                    const chunk = await fo.getChunk(offset, path, dataTask, size);
                    if (signal.aborted || currentFetchId !== fetchIdRef.current) return;

                    if (isFallbackMode) {
                        accumulatedChunks[offset] = chunk;
                    } else {
                        fetchedChunksRef.current[offset] = chunk;
                        appendNextAvailable();

                        if (!isInitialized) {
                            setIsInitialized(true);
                            setLoading(false);
                            // Initial chunk buffered, safe to play
                            setTimeout(() => {
                                playerRef.current?.play();
                            }, 300);
                        }
                        setIsSeeking(false);
                    }
                    return;
                } catch (err) {
                    console.warn(`[FetchPool] Retry ${i + 1} for ${offset}:`, err);
                    if (i === retries - 1) errorOccurred = err;
                    else await new Promise(r => setTimeout(r, Math.min(Math.pow(2, i) * 600, 3000)));
                }
            }
        };

        while (nextFetchOffsetRef.current < totSize && !errorOccurred && !signal.aborted && currentFetchId === fetchIdRef.current) {
            // Buffer management: don't download too many chunks ahead
            const unappendedCount = Object.keys(fetchedChunksRef.current).length;
            if (unappendedCount > 10) {
                await new Promise(r => setTimeout(r, 600));
                continue;
            }

            while (activeDownloads < maxConcurrency && nextFetchOffsetRef.current < totSize && !signal.aborted) {
                const currentOffset = nextFetchOffsetRef.current;
                const remaining = Math.min(chunkSize, totSize - currentOffset);
                nextFetchOffsetRef.current += chunkSize;
                activeDownloads++;

                const promise = downloadChunkWithRetry(currentOffset, remaining)
                    .finally(() => {
                        activeDownloads--;
                        const index = fetchPool.indexOf(promise);
                        if (index > -1) fetchPool.splice(index, 1);

                        if (isFallbackMode) {
                            const downloaded = Object.keys(accumulatedChunks).length;
                            const total = Math.ceil(totSize / chunkSize);
                            setDownloadProgress(Math.min(100, Math.round((downloaded / total) * 100)));
                        }
                    });
                fetchPool.push(promise);
            }

            if (fetchPool.length > 0) await Promise.race(fetchPool);
        }

        await Promise.allSettled(fetchPool);
        if (errorOccurred) {
            console.error("[FetchPool] Terminated due to errors:", errorOccurred);
            setError("[FetchPool] Terminated due to errors:", errorOccurred);
            return null;
        }

        if (isFallbackMode) {
            const sortedOffsets = Object.keys(accumulatedChunks).map(Number).sort((a, b) => a - b);
            const blobParts = sortedOffsets.map(offset => accumulatedChunks[offset]);
            const fileExtension = fileName?.split(".").pop()?.toLowerCase();
            return new Blob(blobParts, { type: getVideoMimeType(fileExtension) || fo.type || "video/mp4" });
        }
        return null;
    };

    const handleSeeking = (e) => {
        if (!isMseModeRef.current) return; // Let native video handle seeking natively
        const videoElement = playerRef.current?.video?.video;
        if (!videoElement) return;

        const seekTime = videoElement.currentTime;

        // Tolerance for buffered check
        let isBuffered = false;
        for (let i = 0; i < videoElement.buffered.length; i++) {
            if (seekTime >= videoElement.buffered.start(i) - 0.5 && seekTime <= videoElement.buffered.end(i) + 1) {
                isBuffered = true;
                break;
            }
        }
        if (isBuffered || !duration) return;

        console.log(`[Video] Seeking to unbuffered ${seekTime.toFixed(1)}s (Resetting fetch pipeline)`);
        setIsSeeking(true);
        fetchIdRef.current++;
        fetchedChunksRef.current = {};

        const sb = sourceBufferRef.current;
        if (sb) {
            if (sb.updating) {
                try { sb.abort(); } catch (e) { console.error("[SourceBuffer] Seek abort error:", e); }
            }
            try {
                if (sb.buffered.length > 0) {
                    isEvictingRef.current = true;
                    // Remove ALL buffered content
                    sb.remove(0, duration || 1000000); // High enough for any video

                    const onUpdateEnd = () => {
                        sb.removeEventListener("updateend", onUpdateEnd);
                        isEvictingRef.current = false;
                        restartFetch(seekTime);
                    };
                    sb.addEventListener("updateend", onUpdateEnd);
                    return;
                }
            } catch (e) {
                console.error("[SourceBuffer] Clear error on seek:", e);
            }
        }

        restartFetch(seekTime);
    };

    const restartFetch = (seekTime) => {
        const targetOffset = Math.floor((seekTime / duration) * totalSizeRef.current);
        const alignedOffset = Math.floor(targetOffset / (4 * 1024 * 1024)) * (4 * 1024 * 1024);

        const signal = abortControllerRef.current.signal;
        const fullStreamPath = determinePath(fo, fileName, dupCurrentPath);

        nextFetchOffsetRef.current = alignedOffset;
        nextAppendOffsetRef.current = alignedOffset;
        startConcurrentFetch(totalSizeRef.current, signal, alignedOffset, fetchIdRef.current, fullStreamPath);
    };

    const cleanup = () => {
        fetchIdRef.current++;
        if (abortControllerRef.current) abortControllerRef.current.abort();
        if (videoUrl) {
            URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
        }
        mediaSourceRef.current = null;
        sourceBufferRef.current = null;
        fetchedChunksRef.current = {};
        nextFetchOffsetRef.current = 0;
        nextAppendOffsetRef.current = 0;
        setIsSeeking(false);
        setIsInitialized(false);
        isEvictingRef.current = false;
        setDownloadProgress(0);
        // Explicitly clear error when closing/reopening
        setError(null);
    };

    // Monitor underlying video element for errors
    useEffect(() => {
        const videoElement = playerRef.current?.video?.video;
        if (!videoElement) return;

        videoElement.disablePictureInPicture = true;
        videoElement.setAttribute("controlsList", "nodownload noplaybackrate");

        const handleVideoError = (e) => {
            const videoError = videoElement.error;
            console.error("[VideoElement] Error event:", videoError);
            const msg = videoError?.message || `Error Code: ${videoError?.code}`;

            const isMseSpecificError = msg.includes("CHUNK_DEMUXER") || msg.includes("unfragmented MP4") || msg.includes("Media Source");

            // If it's a demuxer error (unfragmented MP4) or decode/src error
            if (isMseSpecificError || videoError?.code === 3 || videoError?.code === 4) {
                if (isMseModeRef.current) {
                    console.warn("[VideoElement] Decode/Demux error detected via MSE. Triggering native fallback...");
                    triggerFallbackDownload();
                } else {
                    if (isMseSpecificError || (!videoUrl && videoError?.code === 4)) {
                        console.warn("[VideoElement] Ignoring delayed MSE ghost error during fallback transition.");
                        return; // Ignore the late-arriving MSE error
                    }
                    // Already in native fallback, so show the raw error to the user
                    console.error("[VideoElement] Native decoding failed:", msg);
                    setError(`Playback failed: ${msg} (Native Mode)`);
                }
            } else {
                setError(`Playback failed: ${msg}. Try refreshing or another browser.`);
            }
        };

        videoElement.addEventListener("error", handleVideoError);
        return () => {
            videoElement.removeEventListener("error", handleVideoError);
        };
    }, [videoUrl]);

    if (!isVisible) return null;

    return (
        <div className="video-player-container" data-theme={theme} data-is-mobile={isMobileView}>
            <div className="video-player-layout" data-theme={theme}>
                <div className="video-player-header" data-theme={theme}>
                    <div className="header-left">
                        <img src={theme === "dark" ? logoDark : logo} alt="Induz" className="header-logo" />
                    </div>
                    <div className="header-center">
                        <span className="file-name">{fileName}</span>
                    </div>
                    <div className="header-right">
                        <div className="close-btn" onClick={handleCancel} title="Close">
                            <CloseIcon />
                        </div>
                    </div>
                </div>

                <div className="video-player-content">
                    {error ? (
                        <div className="error-overlay">
                            <div className="error-card">
                                <div className="error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 8V12" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 16H12.01" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3>Oops! Something went wrong</h3>
                                <p>We couldn`&apos;`t load the requested video. This might be due to a network issue or the file might be corrupted.</p>

                                <div className="technical-details">
                                    <details>
                                        <summary>Technical Details</summary>
                                        <div className="error-text">{error}</div>
                                    </details>
                                </div>

                                <div className="error-actions">
                                    <button className="error-retry-btn" onClick={handleRetry}>Retry</button>
                                    <button className="error-close-btn" onClick={handleCancel}>Exit Player</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="player-wrapper" data-theme={theme} onContextMenu={(e) => e.preventDefault()}>
                            {(loading || isSeeking) && (
                                <div className={`loading-overlay ${!isMseModeRef.current && !isSeeking ? 'fallback-loading' : ''}`} data-theme={theme}>
                                    {isMseModeRef.current || isSeeking ? (
                                        <>
                                            <div className="spinner" data-theme={theme}></div>
                                            <p>{isSeeking ? "Seeking to position..." : "Preparing Stream..."}</p>
                                        </>
                                    ) : (
                                        <div className="circular-progress-wrapper">
                                            <div className="spinner" data-theme={theme}></div>
                                            <p className="loading-text">Loading... {downloadProgress}%</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {videoUrl && (
                                <Player
                                    ref={playerRef}
                                    src={videoUrl}
                                    onSeeking={handleSeeking}
                                    onEnded={() => {
                                        const video = playerRef.current?.video?.video;
                                        if (video) {
                                            video.currentTime = 0;
                                            if (video.loop) {
                                                video.play().catch(console.error);
                                            } else {
                                                setIsSeeking(false);
                                                setLoading(false);
                                            }
                                        }
                                    }}
                                    fluid={false}
                                    width="100%"
                                    height="100%"
                                    playsInline
                                    disablePictureInPicture
                                >
                                    <BigPlayButton position="center" title="Play Movie" />
                                    <ControlBar autoHide={true} disableDefaultControls={true}>
                                        <PlayToggle />
                                        <VolumeMenuButton />
                                        <CurrentTimeDisplay />
                                        <TimeDivider />
                                        <DurationDisplay />
                                        <ProgressControl />
                                        <FullscreenToggle />
                                    </ControlBar>
                                </Player>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideoViewer;
